// auth/auth.service.ts
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Injector, input } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, from, Observable, of, throwError } from 'rxjs';
import { concatMap, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { DeviceService, ENVIRONMENT, ImpersonationSession, Logger, Platform, StorageService, User } from 'shared';

declare var cordova: any;

@Injectable({ providedIn: 'root' })
export class AuthService {

  private isAuthenticated = new BehaviorSubject<boolean>(false);
  private user = new BehaviorSubject<User | null>(null);
  private token = new BehaviorSubject<string | null>(null);

  protected logger = new Logger('AuthService');

  constructor(private http: HttpClient,
              private platform: Platform,
              private router: Router,
              private storageService: StorageService,
              private deviceService: DeviceService,
              private injector: Injector,
              @Inject(ENVIRONMENT) private environment: any) {
  }

  login(credentials: { username: string; password: string }): Observable<User> {
    const login = from(this.deviceService.device()).pipe(
      switchMap((device) => {
        const context: any = {
          'device': device,
          'app_version': this.environment.version,
          'path': window.location.pathname
        }
        return this.http.post<{user: User, token?: string}>('/login', {credentials, context}).pipe(
          concatMap(response => {
            const {user, token} = {...response};
            const previousUser = this.getUser();
            if (!user) {
              throw new Error('Login failed');
            }
            const cleanup$ = previousUser?.id !== user.id
              // ? from(this.deleteIndexedDbs()).pipe(tap(() => localStorage.clear())) // performed by ChatService
              ? from(Promise.resolve()).pipe(tap(() => localStorage.clear()))
              : of(undefined);
    
            return cleanup$.pipe(
              concatMap(() =>
                from(this.setAuthState(user, token)).pipe(map(() => user))
              )
            );
          }),
          catchError(error => {
            return from(this.setAuthState(null)).pipe(concatMap(() => throwError(() => error)));
          })
        );
      }),
      shareReplay(1)
    );
    login.subscribe({error: (error) => this.logger.error(error)});
    return login;
  }

  loginWithJwt(jwt: string): Observable<User> {
    const login = this.http
      .post<{
        success: boolean;
        user?: User;
        user_type?: string;
        token?: string;
        goto?: string;
      }>('/sso', { token: jwt }, { observe: 'response' })
      .pipe(
        concatMap(response => {
          const body = response.body!;
          const { success, user, token } = body;
          const previousUser = this.getUser();

          if (!success || !user) {
            throw new Error('Login failed');
          }
          const cleanup$ = previousUser?.id !== user.id
             // ? from(this.deleteIndexedDbs()).pipe(tap(() => localStorage.clear())) // performed by ChatService
             ? from(Promise.resolve()).pipe(tap(() => localStorage.clear()))
             : of(undefined);

          return cleanup$.pipe(
            concatMap(() =>
              from(this.setAuthState(user, token)).pipe(map(() => user))
            )
          );
        }),
        catchError(error => {
          return from(this.setAuthState(null)).pipe(concatMap(() => throwError(() => error)));
        }),
        shareReplay(1)
      );
      login.subscribe({error: (error) => this.logger.error(error)});
      return login;
  }

  logout(): Observable<void> {
    const logout = this.http
      .post<{
          success: boolean;
          goto?: string, 
          message?: string
      }>('/logout', {}, { observe: 'response' })
      .pipe(
        map(() => void 0),
        finalize(() => {
          // this.deleteIndexedDbs().finally(() => {
          Promise.resolve().finally(() => {
            history.replaceState(
              {},             // clear state
              '',             // document title (ignored by modern browsers) 
              this.router.url // keep the current url
            );
            localStorage.clear();
            this.setAuthState(null);
          })
        }),
        shareReplay(1)
      )
      logout.subscribe({error: (error) => this.logger.error(error)});
      return logout; 
  }

  async setAuthState(user: User | null, token: string | null = null): Promise<void> {
    this.logger.debug('setAuthState',  { user, token });
    token = user ? token : null;
    try {
      await this.updateToken(user, token);
      // localStorage.clear();
    }
    catch(error) {
      this.logger.error('setAuthState:', error);
    } 
    finally {
      this.isAuthenticated.next(!!user);
      this.user.next(user);
    }
  }

  isLoggedIn$(): Observable<boolean> {
    return this.isAuthenticated.asObservable();
  }

  getUser$(): Observable<User | null> {
    return this.user.asObservable();
  }

  getToken$(): Observable<string | null> {
    return this.token.asObservable();
  }

  isLoggedIn(): boolean {
    return this.isAuthenticated.getValue();
  }

  getUser(): User | null {
    return this.user.getValue();
  }

  /**
   * Refresh user data from the server
   * Updates the user BehaviorSubject with fresh data
   */
  refreshUser(): Observable<User | null> {
    return this.http.get<{ user: User }>('/user').pipe(
      map(response => {
        if (response.user) {
          this.user.next(response.user);
          return response.user;
        }
        return null;
      }),
      catchError(error => {
        this.logger.error('Failed to refresh user:', error);
        return of(null);
      })
    );
  }

  private async updateToken(user: User | null, token?: string | null): Promise<void> {
    this.logger.debug('updateToken', {user, token});
    if (this.platform.is('hybrid') /*&& this.platform.is('ios')*/) {
      if (user && token) {
        await this.saveToken(token);
      } else if (!user && !token) {
        await this.clearToken();
      }
      if (this.platform.is('ios')) {
        try {
          const url = new URL(this.environment.serverUrl);
          const origin = url.origin;              // e.g., "https://www.mnlo.app"
          const domain = url.hostname;            // e.g., "www.mnlo.app"
          const path = url.pathname === '/' || !url.pathname ? '' : url.pathname;
          const method = 'GET';
          this.syncCookies(method, origin, domain, path);
        } catch (error) {
          this.logger.error('Failed to update token. Invalid URL:', input, error);
        }
      }
      this.token.next(await this.getToken());
    }
  }

  private async saveToken(token: string): Promise<void> {
    this.logger.debug('saveToken', token);
    await this.storageService.set('jwt', token);
  }

  async getToken(): Promise<string | null> {
    const token = await this.storageService.get('jwt');
    this.logger.debug('getToken', token);
    return token;
  }

  private async clearToken(): Promise<void> {
    this.logger.debug('clearToken');
    await this.storageService.remove('jwt');
  }

  private syncCookies(method: string, url: string, domain: string, path: string): Promise<void> {
    return new Promise((resolve, reject) => {
      cordova?.exec(
        () => {
          this.logger.log('Cookie sync successful');
          resolve();
        },
        (error: any) => {
          this.logger.error('Cookie sync failed:', error);
          reject(error);
        },
        'WKWebViewSyncCookies',       // service Name (as defined in plugin.xml)
        'sync',                       // action Name (method in Objective-C)
        [method, url, domain, path]   // arguments (must match what Objective-C expects)
      );
    });
  }

  private deleteIndexedDbs(): Promise<void> {
    if (this.platform.is('ios') && this.platform.is('hybrid')) {
      // matrix chat indexed dbs are replicated to the parent only in ios weview
      // because it does not keep them between app restarts
      const dbs = [
        "matrix-js-sdk::matrix-sdk-crypto",
        "matrix-js-sdk::web-sync-store",
      ];
      return new Promise((resolve, reject) => {
        let remaining = dbs.length;
        let hadError = false;
    
        dbs.forEach((name) => {
          const request = indexedDB.deleteDatabase(name);
    
          request.onsuccess = () => {
            this.logger.info(`Deleted IndexedDB "${name}" (if it existed).`);
            if (--remaining === 0 && !hadError) resolve();
          };
    
          request.onerror = (error) => {
            this.logger.error(`Error deleting IndexedDB "${name}"`, error);
            hadError = true;
            reject(error);
          };
    
          request.onblocked = () => {
            this.logger.warn(`Delete blocked for IndexedDB "${name}"`);
          };
        });
      });
    } else {
      return Promise.resolve();
    }
  }

  /**
   * Start impersonating a user (super admin only)
   * Delegates to ImpersonationService and updates auth state
   */
  impersonateUser(userId: number): Observable<User> {
    // Lazy load service at runtime to avoid circular dependency
    return from(import('app')).pipe(
      switchMap(module => {
        const impersonationService = this.injector.get(module.ImpersonationService);
        return impersonationService.startImpersonation(userId);
      }),
      tap((impersonatedUser: User) => {
        // Update auth state with impersonated user
        this.setAuthState(impersonatedUser, null);
      })
    );
  }

  /**
   * Stop impersonating and return to admin session
   */
  stopImpersonating(): Observable<void> {
    return from(import('app')).pipe(
      switchMap(module => {
        const impersonationService = this.injector.get(module.ImpersonationService);
        return impersonationService.stopImpersonation();
      }),
      switchMap(() => this.http.get<{ user: User }>('/user')),
      tap((response: { user: User }) => {
        // Restore admin user
        if (response.user) {
          this.setAuthState(response.user, null);
        }
      }),
      map(() => void 0)
    );
  }

  /**
   * Check if currently impersonating
   */
  isImpersonating(): boolean {
    // For synchronous check, we need a different approach
    // Check sessionStorage directly
    try {
      const stored = sessionStorage.getItem('impersonation_session');
      if (stored) {
        const session = JSON.parse(stored);
        return session.active === true;
      }
    } catch (e) {
      this.logger.error('Failed to check impersonation status', e);
    }
    return false;
  }

  /**
   * Get impersonation session
   */
  getImpersonationSession(): ImpersonationSession {
    // For synchronous access, read from sessionStorage directly
    try {
      const stored = sessionStorage.getItem('impersonation_session');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      this.logger.error('Failed to get impersonation session', e);
    }
    return { active: false };
  }

  // private checkAuthStatus(): void {
  //   this.http.get('/user').subscribe({
  //     next: (response: any) => {
  //       this.logger.debug('Auth check successful, user is authenticated:', response);
  //       if (response.user) {
  //         this.setAuthState(response.user);
  //       } else {
  //         this.setAuthState(null);
  //       }
  //       this.authCheckCompleted$.next(true);
  //     },
  //     error: (error) => {
  //       this.setAuthState(null);
  //       this.authCheckCompleted$.next(true);
  //     }
  //   });
  // }
}